import React, { FC, memo, useCallback, useMemo, useState, SyntheticEvent } from "react";
import { ExpandMore } from "@mui/icons-material";
import { Button, Chip, Tab, Tabs } from "@mui/material";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { RootState } from "../../../../store/store";
import CustomTabPanel from "../../CustomTabPanel";
import FilterData from "./FilterData";
import styles from "./plansetup.module.scss";

interface PlanProps {
  handleClick: (open: boolean) => void;
}

const PlanSetup: FC<PlanProps> = ({ handleClick }) => {
  // Local UI state
  const [tabValue, setTabValue] = useState<number>(0);
  const [expanded, setExpanded] = useState<boolean>(true);
  const handleToggleChange = useCallback(() => { 
    setExpanded((prev) => !prev);
  }, []);
  const location = useLocation();
  const { sites } = useSelector((state: RootState) => state.site);

  // We choose from either siteParams or kitchenParams based on route.
  const { params } = useSelector((state: RootState) =>
    location.pathname === "/sitemanagerdashboard" ? state.siteParams : state.kitchenParams
  ) || {};

  // Memoize derived values
  const fromDate = useMemo(() => {
    return params?.fromdate ? format(new Date(params.fromdate), "dd MMM yyyy") : "No Date";
  }, [params?.fromdate]);

  const toDate = useMemo(() => {
    return params?.todate ? format(new Date(params.todate), "dd MMM yyyy") : "No Date";
  }, [params?.todate]);

  const siteidlist: string[] = useMemo(() => {
    return Array.isArray(params?.siteidlist) ? params.siteidlist : [];
  }, [params?.siteidlist]);

  const siteItems = useMemo(() => {
    return sites
      .filter((site) => siteidlist.includes(site.sitecode))
      .map((site) => ({
        sitecode: site.sitecode,
        label: site.sitealias || site.sitename,
      }));
  }, [sites, siteidlist]);

  const { regions } = useSelector((state: RootState) => state.region);
  const regionslist = useMemo(() => {
    if (!regions?.length) return [];
    // params.regionid can be a single id (single region) or an array (multi-region).
    const selectedRegionIds = Array.isArray(params?.regionid)
      ? params.regionid.map(String)
      : params?.regionid != null
        ? [String(params.regionid)]
        : [];
    return regions
      .filter((r) => selectedRegionIds.includes(String(r.regionid)))
      .map((r) => ({
        id: r.regionid,
        label: r.regionname,
      }));
  }, [regions, params?.regionid]);

  // Handlers for tab and expand/collapse
  const handleChangeTab = useCallback((_: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  }, []);

  // Render logic for the header
  const isAllSite = params?.isallsite ?? false;
  const hasSiteList = siteidlist.length > 0;

  return (
    <div className="white-box mb-12">
          
        <div
        className={`flex justify-content-between align-items-center ${
          expanded ? styles.tabbingDataWrap : styles.tabbingDataWrapMinimize
        }`}>
        {expanded ? (
          <Tabs
            value={tabValue}
            onChange={handleChangeTab}
            aria-label="Tabs"
            className={expanded ? 'tabs-wrapper' : 'tabs-wrapper colapsed'}
          >
            <Tab label="Filter" disableRipple />
          </Tabs>
        ) : (
          <div className="flex">
            <div className={styles.dateRange}>
              <span className={styles.dateLabel}>Menu Period:</span>
              <span className={styles.date}>{fromDate}</span>-
              <span className={styles.date}>{toDate}</span>
            </div>
            <div className={`selecte-sites flex align-items-center justify-content-start ${styles.selectedSites}`}>
              <span className={styles.dateLabel}>Region:</span>
              <span className={styles.chipData}>
                {regionslist.length > 0
                  ? regionslist.map((region) => (
                      <Chip key={region.id} label={region.label} className={styles.chip} />
                    ))
                  :  (
                  <Chip label="All" className={styles.chip} />
                )}
              </span>               
            </div>
            <div className={`selecte-sites flex align-items-center justify-content-start ${styles.selectedSites}`}>
              <span className={styles.dateLabel}>Selected Site:</span>
              <span className={styles.chipData}>
                {isAllSite && hasSiteList ? (
                  <Chip key={"ALL_SITES"} label={"All Sites"} className={styles.chip} />
                ) : siteItems.length > 0 ? (
                  siteItems.map((item) => (
                    <Chip key={item.sitecode} label={item.label} className={styles.chip} />
                  ))
                ) : (
                  <Chip label="No Sites" className={styles.chip} />
                )}
              </span>
            </div>
          </div>
        )}
        <Button onClick={handleToggleChange} className={`expend-btn ${expanded ? "active" : ""}`} disableRipple>
          {expanded ? "Minimize Section" : "Expand"}
          <span>
            <ExpandMore />
          </span>
        </Button>
      </div>
      <div className={`inner-contant ${!expanded ? "d-none" : ""}`}>
        <CustomTabPanel value={tabValue} index={0}>
          {/* Pass the handleClick down to FilterData */}
          <FilterData handleClick={handleClick} handleMenuChange={handleToggleChange} />
        </CustomTabPanel>
      </div>
    </div>
  );
};

export default memo(PlanSetup);
